import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useAuthContext } from '../../hooks/useAuthContext';
import { checkoutApi } from '../../api/checkoutApi';
import Navbar from '../Navbar';
import ArtozphereCartBar from './ArtozphereCartBar';

const ArtozphereCheckout = () => {
  const { cartItems, getCartTotal } = useCart();
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const formRef = useRef();

  const [form, setForm] = useState({
    name: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    zip: '',
    country: '',
    delivery: 'standard',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      setForm((prev) => ({
        ...prev,
        name: user.firstName || prev.name,
        lastName: user.lastName || prev.lastName,
        email: user.email || prev.email,
      }));
    }
  }, [user]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      alert('Please log in to complete your purchase.');
      navigate('/login');
      return;
    }

    if (cartItems.length === 0) {
      alert('Your cart is empty.');
      navigate('/artozphere/cart');
      return;
    }

    setSubmitting(true);
    try {
      const data = await checkoutApi.createCheckoutSession({
        form,
        cartItems,
        returnBaseUrl: window.location.origin,
        successPath: '/artozphere/success',
        cancelPath: '/artozphere/cart',
      });
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert(data.message || data.error || 'Failed to initiate payment.');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert(
        error.response?.data?.message ||
          error.response?.data?.error ||
          'An error occurred. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4">
      <Navbar />
      <div className="ml-0 md:ml-20 max-w-6xl mx-auto pb-12">
        <div className="flex items-center justify-between pt-8 mb-10">
          <h1 className="text-3xl font-extrabold">CHECKOUT</h1>
          <ArtozphereCartBar />
        </div>

        <div className="flex flex-col lg:flex-row gap-12">
          <form
            ref={formRef}
            onSubmit={handleSubmit}
            className="w-full lg:w-2/3 space-y-10 text-sm"
          >
            <div>
              <h2 className="font-bold text-lg mb-4">MY INFORMATION</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block mb-1 font-medium">First name *</label>
                  <input
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    className="w-full bg-gray-200 px-4 py-2 rounded"
                  />
                </div>
                <div>
                  <label className="block mb-1 font-medium">Last name *</label>
                  <input
                    name="lastName"
                    value={form.lastName}
                    onChange={handleChange}
                    required
                    className="w-full bg-gray-200 px-4 py-2 rounded"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block mb-1 font-medium">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-gray-200 px-4 py-2 rounded"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block mb-1 font-medium">Contact Number *</label>
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    required
                    className="w-full bg-gray-200 px-4 py-2 rounded"
                  />
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-gray-500 font-bold text-md mb-2">BILLING ADDRESS</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-200 px-4 py-2 rounded"
                  placeholder="Street Address"
                />
                <input
                  name="city"
                  value={form.city}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-200 px-4 py-2 rounded"
                  placeholder="City"
                />
                <input
                  name="zip"
                  value={form.zip}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-200 px-4 py-2 rounded"
                  placeholder="Zip Code"
                />
                <input
                  name="country"
                  value={form.country}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-200 px-4 py-2 rounded"
                  placeholder="Country"
                />
              </div>
            </div>

            <div>
              <h2 className="text-gray-500 font-bold text-md mb-2">DELIVERY</h2>
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="delivery"
                    value="standard"
                    checked={form.delivery === 'standard'}
                    onChange={handleChange}
                  />
                  Standard Delivery
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="delivery"
                    value="express"
                    checked={form.delivery === 'express'}
                    onChange={handleChange}
                  />
                  Express Delivery
                </label>
              </div>
            </div>

            <button type="submit" className="hidden" />
          </form>

          <div className="w-full lg:w-1/3 space-y-6 border-t lg:border-t-0 lg:border-l lg:pl-8">
            <h2 className="font-bold text-lg">Order Summary</h2>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Order Value</span>
                <span>${getCartTotal().toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Estimated Delivery Fee</span>
                <span className="text-gray-600">FREE</span>
              </div>
            </div>
            <div className="border-t pt-4 mt-4">
              <div className="flex justify-between font-semibold text-lg">
                <span>Total</span>
                <span>${getCartTotal().toFixed(2)}</span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => formRef.current?.requestSubmit()}
              disabled={submitting || cartItems.length === 0}
              className="w-full bg-gray-700 text-white py-3 rounded hover:bg-black transition disabled:opacity-50"
            >
              {submitting ? 'PROCESSING...' : 'COMPLETE PURCHASE'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArtozphereCheckout;
